import { Button, Card } from '@payaca/ambrosia';
import { FC, useCallback, useEffect, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import { usePermissions } from '@/hooks/usePermissions';
import { getTimelogTypes } from '@payaca/store/timelogs/timelogsActions';
import {
  PublicTimelogType,
  TimelogTypeRequiredSystemCategories,
} from '@payaca/types/timelogs';
import CreateEditTimelogTypeControl from '../createEditTimelogTypeControl/CreateEditTimelogTypeControl';
import ManageableTimelogType from '../manageableTimelogType/ManageableTimelogType';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import './CompanySettingsTimeTracking.sass';

type Props = {
  readOnly: boolean;
};

const CompanySettingsTimeTracking: FC<Props> = ({
  readOnly,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const { userHasRequiredPermission } = usePermissions();
  const canManageTimelogTypes = userHasRequiredPermission([
    TimelogsPermissions.MANAGE_TIMELOG_TYPES,
  ]);

  const [timelogTypes, setTimelogTypes] = useState<PublicTimelogType[]>([]);
  const [isInputtingNewType, setIsInputtingNewType] = useState(false);
  const [isGettingTimelogTypes, setIsGettingTimelogTypes] = useState(false);

  const handleGetTimelogTypes = useCallback(() => {
    setIsGettingTimelogTypes(true);
    dispatch(
      getTimelogTypes.request({
        callback: (tt) => {
          setTimelogTypes(tt);
          setIsGettingTimelogTypes(false);
        },
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, []);

  useEffect(() => {
    handleGetTimelogTypes();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'handleGetTimelogTypes'
  }, []);

  return (
    <Card sizeVariant="lg">
      <Card.Body className="company-settings-time-tracking">
        <h2>Timelog types</h2>
        <span>
          Users can choose from this list of types when they record a timelog.
        </span>
        {!!timelogTypes?.length && (
          <ul className="timelog-types-list">
            {timelogTypes.map((tt, i) => {
              const isLastTimelogTypeOfRequiredCategory =
                timelogTypes.filter(
                  (timelogType) =>
                    tt.category !== 'uncategorised' &&
                    timelogType.category === tt.category &&
                    TimelogTypeRequiredSystemCategories.includes(tt.category)
                ).length === 1;

              return (
                <li key={i}>
                  <ManageableTimelogType
                    timelogType={tt}
                    isReadOnly={readOnly && !canManageTimelogTypes}
                    onArchiveSuccess={handleGetTimelogTypes}
                    onUpdateSuccess={handleGetTimelogTypes}
                    isArchivable={!isLastTimelogTypeOfRequiredCategory}
                  />
                </li>
              );
            })}
          </ul>
        )}
        {!isGettingTimelogTypes && !timelogTypes?.length && (
          <span>{"You haven't added any timelog types yet."}</span>
        )}
        <PermissionGuard
          renderIfHasPermissions={[TimelogsPermissions.MANAGE_TIMELOG_TYPES]}
        >
          <>
            {isInputtingNewType && (
              <div>
                <h5>Adding a new type</h5>
                <CreateEditTimelogTypeControl
                  onCreateEditSuccess={() => {
                    handleGetTimelogTypes();
                    setIsInputtingNewType(false);
                  }}
                />
              </div>
            )}
            {!isInputtingNewType && (
              <Button
                variant="link"
                onClick={() => setIsInputtingNewType(true)}
              >
                Add a new type
              </Button>
            )}
          </>
        </PermissionGuard>
      </Card.Body>
    </Card>
  );
};

export default CompanySettingsTimeTracking;
